// Client portal — read-only, censored view of a single client account.
//
// Mirrors the admin ClientDetail layout (header + tab strip + tab content)
// but pulls every datum from /api/me/* so:
//   • the user can only ever see their own client(s)
//   • carrier_cost, margins, and other internal fields are stripped server-side
// All write actions are absent — the portal is read-only by design.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Building2, Loader2, ShoppingCart, Box, RotateCw, Globe,
  Database, Truck, Activity, AlertTriangle, DollarSign, Calculator,
  Clock, MapPin, Package, Tags,
} from 'lucide-react'
import { format, parseISO, isValid } from 'date-fns'
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, ResponsiveContainer,
} from 'recharts'
import OrderTable from '../../components/OrderTable'
import FilterBar from '../../components/FilterBar'
import StorageTab from './tabs/StorageTab'
import ReturnsTab from './tabs/ReturnsTab'
import SelfShipTab from './tabs/SelfShipTab'
import ExportsTab from './tabs/ExportsTab'
import PackagingTab from './tabs/PackagingTab'
import DiscrepanciesTab from './tabs/DiscrepanciesTab'
import ActivityTab from './tabs/ActivityTab'
import RateBrowserTab from './tabs/RateBrowserTab'
import ProductsTab from '../../components/ProductsTab'
import {
  getMe, getMeClient, getMePricing, getMeMtdCounts, getMeMtdSummary,
  getMeClientDailyMetrics,
  getMeClientCycleTime, getMeClientDayOfWeek,
  getMeClientTopDestinations, getMeClientTopSkus,
  getMeOrders, getMeOrderCarriers, getMeOrderStates, getMyOrderTrackingEvents,
  getMeProductsAccess,
} from '../../lib/api'
import { businessToday, addDays } from '../../lib/dates'

// Positional carrier palette — mirrors the admin tracking overview so a
// client sees the same visual language their account manager works in.
// Assignment is `carrier[i] → CARRIER_COLOR[i % 9]` after alphabetical
// sort (a stable ordering means the same carrier keeps the same hue on
// every render). Kept local because no shared chart palette exists.
const CARRIER_COLOR = [
  '#2563eb', '#7c3aed', '#10b981', '#f59e0b', '#dc2626',
  '#0891b2', '#db2777', '#65a30d', '#475569',
]

// Spend chart — two-colour stack. Emerald for shipping (matches the
// "delivered" hue on the tracking donut), amber for fulfilment.
const SPEND_COLOR = { Shipping: '#10b981', Fulfilment: '#f59e0b' }

// Tabs deliberately match the admin ClientDetail set so a client gets the
// same surface area their account manager works in (sans editing).
const TABS = [
  { key: 'overview',      label: 'Overview',     icon: Building2 },
  { key: 'orders',        label: 'Orders',       icon: ShoppingCart },
  { key: 'self-ship',     label: 'Self Ship',    icon: Truck },
  { key: 'returns',       label: 'Returns',      icon: RotateCw },
  { key: 'exports',       label: 'Exports',      icon: Globe },
  { key: 'storage',       label: 'Storage',      icon: Database },
  { key: 'packaging',     label: 'Packaging',    icon: Box },
  { key: 'warehouse',     label: 'Activity',     icon: Activity },
  { key: 'discrepancies', label: 'Discrepancies', icon: AlertTriangle },
  // Rate Browser hidden from the client portal — WIP, re-enable later
  // (component + /me rate-quote endpoint are intact; just uncomment).
  // { key: 'rate-browser',  label: 'Rate Browser',  icon: Calculator },
]

export default function Account() {
  const { clientId: routeClientId, tab: routeTab } = useParams()
  const navigate = useNavigate()
  // Whitelist the incoming tab against TABS so a hand-typed /account/x/foo
  // can't render a blank body — anything unknown falls back to overview.
  // Products is only listed once OPS switches it on for this client (or for a
  // feature-allowed admin previewing the client view) — see productsAccess.
  const TAB_KEYS = [...TABS.map(t => t.key), 'products']
  const requestedTab = routeTab && TAB_KEYS.includes(routeTab) ? routeTab : 'overview'

  // Identity + accessible client list
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['me'],
    queryFn: getMe,
    staleTime: 60_000,
  })

  // If the URL has no client_id but the user has clients, redirect to the first.
  // If the URL has one that the user doesn't have access to, swap to the first.
  useEffect(() => {
    if (!me) return
    const allowed = (me.clients || []).map(c => c.id)
    if (allowed.length === 0) return
    if (!routeClientId || !allowed.includes(routeClientId)) {
      navigate(`/account/${allowed[0]}/${requestedTab}`, { replace: true })
    }
  }, [me, routeClientId, requestedTab, navigate])

  const clientId = useMemo(() => {
    const allowed = (me?.clients || []).map(c => c.id)
    return routeClientId && allowed.includes(routeClientId) ? routeClientId : allowed[0]
  }, [me, routeClientId])

  const { data: productsAccess } = useQuery({
    queryKey: ['me', 'products-access', clientId],
    queryFn:  () => getMeProductsAccess(clientId),
    enabled:  !!clientId,
    retry:    false,
  })
  const tabs = productsAccess?.visible
    ? [...TABS.slice(0, TABS.findIndex(t => t.key === 'packaging') + 1),
       { key: 'products', label: 'Products', icon: Tags },
       ...TABS.slice(TABS.findIndex(t => t.key === 'packaging') + 1)]
    : TABS
  const activeTab = requestedTab === 'products' && !productsAccess?.visible ? 'overview' : requestedTab

  const { data: client } = useQuery({
    queryKey: ['me', 'client', clientId],
    queryFn:  () => getMeClient(clientId),
    enabled:  !!clientId,
  })

  const { data: pricing, error: pricingError } = useQuery({
    queryKey: ['me', 'pricing', clientId],
    queryFn:  () => getMePricing(clientId),
    enabled:  !!clientId,
  })

  if (meLoading) return <LoadingSplash />
  if (!me) return <p className="p-8 text-slate-500">Failed to load account.</p>
  if ((me.clients || []).length === 0) {
    return (
      <div className="p-8 max-w-xl">
        <h2 className="text-xl font-semibold text-slate-800">Account not linked</h2>
        <p className="text-sm text-slate-500 mt-2">
          Your login isn't connected to a client account yet. Contact your account
          manager and they'll get you set up.
        </p>
      </div>
    )
  }

  function setTab(tabKey) {
    navigate(`/account/${clientId}/${tabKey}`)
  }

  return (
    <div className="space-y-6 sm:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{client?.name || 'Your account'}</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Read-only view of your fulfilment data. Reach out to your account
            manager for changes.
          </p>
        </div>
        {(me.clients || []).length > 1 && (
          <select
            value={clientId}
            onChange={e => navigate(`/account/${e.target.value}/${activeTab}`)}
            className="text-sm border border-slate-200 rounded-md px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {me.clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Tab strip — the old 2-stat MtdHeader was retired; its numbers
          (orders + items) now live inside the richer "MTD Numbers" card
          rendered by the Overview tab, mirroring the admin layout. */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {tabs.map(t => (
          <TabButton key={t.key} active={activeTab === t.key} onClick={() => setTab(t.key)} icon={t.icon}>
            {t.label}
          </TabButton>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {/* key={clientId} on tabs that own local filter/page/edit state so
            switching clients tears the component down instead of leaking
            state (e.g. page 4 filters, half-typed inventory row) across
            the boundary. */}
        {activeTab === 'overview'      && <OverviewTab pricing={pricing} pricingError={pricingError} clientId={clientId} />}
        {activeTab === 'orders'        && <OrdersTab key={clientId} clientId={clientId} />}
        {activeTab === 'self-ship'     && <SelfShipTab clientId={clientId} />}
        {activeTab === 'returns'       && <ReturnsTab clientId={clientId} />}
        {activeTab === 'exports'       && <ExportsTab clientId={clientId} />}
        {activeTab === 'storage'       && <StorageTab key={clientId} clientId={clientId} />}
        {activeTab === 'packaging'     && <PackagingTab key={clientId} clientId={clientId} />}
        {activeTab === 'products'      && <ProductsTab key={clientId} clientId={clientId} clientName={client?.name} mode="client" preview={!!productsAccess?.preview} />}
        {activeTab === 'warehouse'     && <ActivityTab clientId={clientId} />}
        {activeTab === 'discrepancies' && <DiscrepanciesTab key={clientId} clientId={clientId} />}
        {/* Rate Browser hidden (WIP): {activeTab === 'rate-browser' && <RateBrowserTab clientId={clientId} />} */}
      </div>
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function LoadingSplash() {
  return (
    <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
      <Loader2 size={18} className="animate-spin" />
      <span className="text-sm">Loading…</span>
    </div>
  )
}

// Shared failure block for this page. A failed useQuery leaves `data`
// undefined, and the `?? 0` / `|| []` defaults downstream turn that into a
// confident $0.00, an "Account current" note, or "No shipments in the last 30
// days" — a load failure must never be shown as a real, reassuring answer.
function QueryError({ error, what, className = '' }) {
  const detail = error?.response?.data?.detail || error?.message || 'Unknown error'
  return (
    <div className={`flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 ${className}`}>
      <AlertTriangle size={14} className="text-red-500 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-xs font-semibold text-red-700">Couldn't load {what}</p>
        <p className="text-xs text-red-600 mt-0.5">
          This is a load failure, not an empty result — the real figures may be higher.
        </p>
        <p className="text-xs text-red-500/80 mt-0.5 font-mono break-all">{detail}</p>
      </div>
    </div>
  )
}

function TabButton({ active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
        active ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      <Icon size={14} />
      {children}
    </button>
  )
}

// ─── Overview tab (MTD cards + Pricing) ─────────────────────────────────────
//
// Layout mirrors the admin ClientDetail header so a client sees the same
// three header cards their account manager does — but margins, costs, and
// other internal numbers are scrubbed server-side.

function OverviewTab({ pricing, pricingError, clientId }) {
  const fmt = (v) => v == null ? '—'
    : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(v)

  const { data: summary, error: summaryError } = useQuery({
    queryKey: ['me', 'mtd-summary', clientId],
    queryFn:  () => getMeMtdSummary(clientId),
    enabled:  !!clientId,
    refetchInterval: 120_000,
  })
  const { data: counts } = useQuery({
    queryKey: ['me', 'mtd-counts', clientId],
    queryFn:  () => getMeMtdCounts(clientId),
    enabled:  !!clientId,
    refetchInterval: 120_000,
  })
  const { data: daily, isLoading: dailyLoading, error: dailyError } = useQuery({
    queryKey: ['me', 'daily-metrics', clientId],
    queryFn:  () => getMeClientDailyMetrics(clientId, 30),
    enabled:  !!clientId,
    refetchInterval: 120_000,
  })
  const { data: cycle, isLoading: cycleLoading, error: cycleError } = useQuery({
    queryKey: ['me', 'cycle-time', clientId],
    queryFn:  () => getMeClientCycleTime(clientId, 30),
    enabled:  !!clientId,
    refetchInterval: 120_000,
  })
  const { data: dow, isLoading: dowLoading, error: dowError } = useQuery({
    queryKey: ['me', 'day-of-week', clientId],
    queryFn:  () => getMeClientDayOfWeek(clientId, 30),
    enabled:  !!clientId,
    refetchInterval: 120_000,
  })
  const { data: destinations, isLoading: destLoading, error: destError } = useQuery({
    queryKey: ['me', 'top-destinations', clientId],
    queryFn:  () => getMeClientTopDestinations(clientId, 30, 10),
    enabled:  !!clientId,
    refetchInterval: 120_000,
  })
  const { data: skus, isLoading: skusLoading, error: skusError } = useQuery({
    queryKey: ['me', 'top-skus', clientId],
    queryFn:  () => getMeClientTopSkus(clientId, 30, 20),
    enabled:  !!clientId,
    refetchInterval: 120_000,
  })

  // Pivot the flat per-(day, carrier, service) rollup into two chart-friendly
  // shapes. Both charts run off the SAME 30-day date spine so bars line up
  // day-for-day across the pair and empty days still render (as zero-height
  // bars) instead of squeezing the axis.
  const { parcelsData, parcelCarriers, spendData, hasAny, servicesByDayCarrier } = useMemo(() => {
    const rows = daily || []
    // Build the axis: the last 30 AU business days ending TODAY.
    //
    // This used to take local midnight and render it through toISOString(),
    // which for any UTC+ browser is the previous UTC day — so the spine ran
    // today-30..today-1 and never contained today at all. Rows join onto it
    // by indexOf (below) and a miss is skipped silently, so today's parcels
    // and spend were dropped from the charts every day, while the MTD cards
    // on the same page (business_today() at routers/me.py:1259/:1313) counted
    // them. That is the discrepancy this fixes.
    //
    // Must stay in lockstep with the backend cutoff, which is now
    // business_today() - (days - 1) at routers/me.py — same 30 dates, so
    // every returned row lands on a slot.
    const spine = []
    const today = businessToday()
    for (let i = 29; i >= 0; i--) spine.push(addDays(today, -i))

    const carriersSet = new Set()
    for (const r of rows) carriersSet.add(r.carrier || 'unknown')
    const carriers = [...carriersSet].sort()

    // parcels: { date, <carrier1>: count, <carrier2>: count, ... }
    const parcels = spine.map(date => {
      const row = { date }
      for (const c of carriers) row[c] = 0
      return row
    })
    // service breakdown per (day, carrier) — powers the parcels tooltip.
    const svcMap = {}
    for (const r of rows) {
      const idx = spine.indexOf(r.date)
      if (idx < 0) continue
      const carrier = r.carrier || 'unknown'
      parcels[idx][carrier] = (parcels[idx][carrier] || 0) + r.parcel_count
      const key = `${r.date}|${carrier}`
      if (!svcMap[key]) svcMap[key] = []
      svcMap[key].push({ service: r.service || 'unknown', count: r.parcel_count })
    }

    // spend: { date, Shipping, Fulfilment } — collapse carriers/services.
    const spend = spine.map(date => ({ date, Shipping: 0, Fulfilment: 0 }))
    for (const r of rows) {
      const idx = spine.indexOf(r.date)
      if (idx < 0) continue
      spend[idx].Shipping   += Number(r.shipping_cost_total   || 0)
      spend[idx].Fulfilment += Number(r.fulfilment_cost_total || 0)
    }
    for (const row of spend) {
      row.Shipping   = Math.round(row.Shipping   * 100) / 100
      row.Fulfilment = Math.round(row.Fulfilment * 100) / 100
    }

    return {
      parcelsData:            parcels,
      parcelCarriers:         carriers,
      spendData:              spend,
      hasAny:                 rows.length > 0,
      servicesByDayCarrier:   svcMap,
    }
  }, [daily])

  return (
    <div className="space-y-4">
      {/* MTD summary — 3 cards, same shape admin sees per client. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Card 1 — MTD Total + revenue breakdown */}
        <div className="card p-4">
          <div className="flex items-start justify-between">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">MTD Total</p>
            <DollarSign size={16} className="text-blue-600" />
          </div>
          {/* The `?? 0` defaults below would render a failed summary fetch as a
              full $0.00 breakdown, so the failure gets its own case first. */}
          {summaryError ? (
            <QueryError error={summaryError} what="your MTD summary" className="mt-2" />
          ) : (
            <>
              <p className="text-2xl font-bold text-slate-800 tabular-nums mt-1">{fmt(summary?.mtd_total)}</p>
              <div className="mt-3 flex flex-col gap-0.5">
                <BreakdownRow label="Order Fulfilment" value={fmt(summary?.mtd_orders_fulfilment ?? 0)} />
                <BreakdownRow label="Order Shipping"   value={fmt(summary?.mtd_orders_shipping ?? 0)} />
                <BreakdownRow label="Order Details"    value={fmt(summary?.mtd_sku_charges ?? 0)} />
                <BreakdownRow label="Returns"          value={fmt(summary?.mtd_returns ?? 0)} />
                <BreakdownRow label="Exports"          value={fmt(summary?.mtd_exports ?? 0)} />
                <BreakdownRow label="Storage"          value={fmt(summary?.mtd_storage ?? 0)} />
                <BreakdownRow label="Activity"         value={fmt(summary?.mtd_activity ?? 0)} />
              </div>
            </>
          )}
        </div>

        {/* Card 2 — MTD order quantity breakdown */}
        <div className="card p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">MTD Numbers</p>
          <div className="flex flex-col gap-1.5">
            {[
              { label: 'Orders',         value: counts?.total_orders   ?? '—' },
              { label: '+Items',         value: counts?.add_items      ?? '—' },
              { label: 'Inserts',        value: counts?.std_inserts    ?? '—' },
              { label: 'Custom Inserts', value: counts?.custom_inserts ?? '—' },
              { label: 'No Insert',      value: counts?.no_inserts     ?? '—' },
              { label: 'Case Picks',     value: counts?.case_picks     ?? '—' },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between items-center">
                <span className="text-xs text-slate-500">{label}</span>
                <span className="text-sm font-semibold text-slate-800 tabular-nums">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Card 3 — Outstanding + Last Sync.
            Red only when xero_overdue=true (past due date) to match admin.
            Coloring on outstanding>0 alone falsely brands current-but-unpaid
            invoices as delinquent. */}
        <div className="card p-4 flex flex-col gap-4">
          {/* Error case first: with `summary` undefined the markup below reads
              "— / Account current / Never", which is a reassuring answer to a
              question that was never answered. */}
          {summaryError ? (
            <QueryError error={summaryError} what="your account status" />
          ) : (
            <>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Outstanding</p>
                <p className={`text-2xl font-bold ${summary?.overdue ? 'text-red-600' : 'text-slate-800'}`}>
                  {fmt(summary?.outstanding)}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {summary?.outstanding > 0
                    ? (summary?.overdue ? 'Past due' : 'Payment pending')
                    : 'Account current'}
                </p>
              </div>
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Last Sync</p>
                {(() => {
                  // Guard parseISO — a malformed or empty last_sync would throw
                  // RangeError out of format() and unmount the whole tab.
                  const d = summary?.last_sync ? parseISO(summary.last_sync) : null
                  const ok = d && isValid(d)
                  return (
                    <>
                      <p className="text-lg font-bold text-slate-800">
                        {ok ? format(d, 'dd MMM HH:mm') : 'Never'}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {ok ? format(d, 'yyyy') : 'No sync data'}
                      </p>
                    </>
                  )
                })()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Daily charts — parcels shipped and spend, side-by-side on lg. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          title="Parcels shipped"
          caption="Last 30 days"
          loading={dailyLoading}
          error={dailyError}
          errorWhat="parcels shipped"
          empty={!dailyLoading && !hasAny}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={parcelsData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickFormatter={d => format(parseISO(d), 'd MMM')}
                minTickGap={20}
              />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                labelFormatter={d => format(parseISO(d), 'EEE d MMM yyyy')}
                content={<ParcelsTooltip services={servicesByDayCarrier} />}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              {parcelCarriers.map((c, i) => (
                <Bar
                  key={c}
                  dataKey={c}
                  stackId="carriers"
                  fill={CARRIER_COLOR[i % CARRIER_COLOR.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Shipping + fulfilment spend"
          caption="Last 30 days"
          loading={dailyLoading}
          error={dailyError}
          errorWhat="shipping + fulfilment spend"
          empty={!dailyLoading && !hasAny}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={spendData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickFormatter={d => format(parseISO(d), 'd MMM')}
                minTickGap={20}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickFormatter={v => fmt(v)}
                width={70}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                labelFormatter={d => format(parseISO(d), 'EEE d MMM yyyy')}
                content={<SpendTooltip fmt={fmt} />}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="Shipping"   stackId="spend" fill={SPEND_COLOR.Shipping}   />
              <Bar dataKey="Fulfilment" stackId="spend" fill={SPEND_COLOR.Fulfilment} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Fulfilment insights — cycle time, day-of-week, top destinations.
          Three equal columns on lg, stacks below the daily bar charts and
          above the pricing card. Each card is self-contained (own loading
          + empty state) so a slow endpoint doesn't hold up the others. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CycleTimeCard cycle={cycle} loading={cycleLoading} error={cycleError} />
        <ChartCard
          title="Ship days"
          caption="Parcels by day-of-week"
          loading={dowLoading}
          error={dowError}
          errorWhat="the ship-day breakdown"
          empty={!dowLoading && !(dow && dow.some(d => d.count > 0))}
          height={200}
        >
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dow || []} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                cursor={{ fill: '#f1f5f9' }}
                formatter={(v) => [new Intl.NumberFormat('en-AU').format(v), 'Parcels']}
              />
              <Bar dataKey="count" fill={CARRIER_COLOR[0]} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard
          title="Top destinations"
          caption="By recipient state"
          loading={destLoading}
          error={destError}
          errorWhat="top destinations"
          empty={!destLoading && !(destinations && destinations.length > 0)}
          height={200}
        >
          <ResponsiveContainer width="100%" height={Math.max(200, (destinations?.slice(0, 8).length || 0) * 24)}>
            <BarChart
              data={(destinations || []).slice(0, 8)}
              layout="vertical"
              margin={{ top: 5, right: 20, bottom: 5, left: 10 }}
            >
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="state"
                tick={{ fontSize: 11, fill: '#64748b' }}
                /* 60px accommodates 2-3 char state codes AND the 7-char
                   "Unknown" bucket the backend emits when recipient_state
                   is NULL — 44px was clipping it. */
                width={60}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                cursor={{ fill: '#f1f5f9' }}
                content={<DestinationsTooltip />}
              />
              <Bar dataKey="count" fill="#7c3aed" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top SKUs — full-width table with a show-more toggle so the default
          fold stays a manageable 10 rows. Progress bar on % of volume gives
          a visual weight without stealing tabular density. */}
      <TopSkusCard skus={skus} loading={skusLoading} error={skusError} />

      {/* Pricing card — full width below the MTD cards */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Your pricing</h3>
        <p className="text-xs text-slate-400 mb-3">
          These are the rates we bill against your account. Tiered fees show the
          per-order rate at increasing volume bands.
        </p>
        {/* Error case first: with `pricing` undefined, TierTable prints
            "No tiers configured." and the flat return rate prints "Not
            configured" — statements about how this client is billed, made
            from no data at all. */}
        {pricingError ? (
          <QueryError error={pricingError} what="your pricing" />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Pick fee (per order)</p>
              <TierTable tiers={pricing?.pick_fee_tiers} />
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mt-4 mb-2">Additional-item fee (per extra item)</p>
              <TierTable tiers={pricing?.additional_item_fee_tiers} />
            </div>
            <div className="space-y-3 text-sm lg:pl-4 lg:border-l lg:border-slate-100">
              <Row label="Labelling fee"         value={fmt(pricing?.labelling_fee)} />
              <Row label="Case pick fee"         value={fmt(pricing?.case_pick_fee)} />
              <Row label="Insert (standard)"     value={fmt(pricing?.insert_fee)} />
              <Row label="Insert (custom)"       value={fmt(pricing?.custom_insert_fee)} />
              <Row label="Flat return rate"      value={pricing?.flat_return_rate ? fmt(pricing.flat_return_rate) : 'Not configured'} />
              <Row label="Tag fee"               value={fmt(pricing?.tag_fee)} />
              <Row label="Insert trigger SKU"    value={pricing?.insert_trigger_sku || '—'} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Card wrapper for the overview charts. Owns the title/caption header,
// the loading spinner, and the empty-state message so the chart body only
// has to render its <ResponsiveContainer>. `height` (default 300) sizes
// the loading + empty placeholders — pass 200 for the shorter side-by-side
// insight cards under the daily bar charts.
function ChartCard({ title, caption, loading, empty, error, errorWhat, height = 300, children }) {
  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        <span className="text-xs text-slate-400">{caption}</span>
      </div>
      {loading ? (
        <div
          className="flex items-center justify-center text-slate-400 gap-2"
          style={{ height }}
        >
          <Loader2 size={16} className="animate-spin" />
          <span className="text-xs">Loading…</span>
        </div>
      ) : error ? (
        // Ahead of the `empty` case, never after it: a failed fetch leaves the
        // chart data empty, so `empty` would claim "No shipments in the last
        // 30 days" and this branch would never be reached.
        <div className="flex items-center" style={{ height }}>
          <QueryError error={error} what={errorWhat} className="w-full" />
        </div>
      ) : empty ? (
        <div
          className="flex items-center justify-center text-xs text-slate-400"
          style={{ height }}
        >
          No shipments in the last 30 days
        </div>
      ) : (
        children
      )}
    </div>
  )
}

// Cycle-time (order → ship latency). One prominent avg number, a
// median + sample subtitle, and a tiny sparkline of the day-by-day avg
// underneath. Zero-day gaps in `cycle.daily` are simply omitted rather
// than zero-filled — a flat line to zero on a slow week would be a lie.
function CycleTimeCard({ cycle, loading, error }) {
  const daily = cycle?.daily || []
  const avg    = cycle?.avg_hours    ?? 0
  const median = cycle?.median_hours ?? 0
  const n      = cycle?.sample_count ?? 0
  // Any daily point is enough — Recharts renders a single-point line as
  // a lone dot when we set `dot={true}`, so a client with one ship-day
  // in the window still sees a mark instead of a text apology.
  const hasSpark = !loading && daily.length >= 1
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Cycle time</h3>
          <p className="text-xs text-slate-400 mt-0.5">Order → ship, last 30 days</p>
        </div>
        <Clock size={16} className="text-blue-600" />
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-[112px] text-slate-400 gap-2">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-xs">Loading…</span>
        </div>
      ) : error ? (
        // Ahead of the n === 0 case: on failure `sample_count` falls back to 0,
        // so the empty branch would otherwise swallow the error.
        <div className="flex items-center h-[112px]">
          <QueryError error={error} what="cycle time" className="w-full" />
        </div>
      ) : n === 0 ? (
        <div className="flex items-center justify-center h-[112px] text-xs text-slate-400">
          No shipments in the last 30 days
        </div>
      ) : (
        <>
          <p className="text-2xl font-bold text-slate-800 tabular-nums mt-2">
            {avg.toFixed(1)} <span className="text-base font-medium text-slate-500">hrs</span>
          </p>
          <p className="text-xs text-slate-500 mt-1 tabular-nums">
            Median {median.toFixed(1)} hrs · {new Intl.NumberFormat('en-AU').format(n)} shipments
          </p>
          <div className="h-[50px] mt-2 -mx-1">
            {hasSpark ? (
              <ResponsiveContainer width="100%" height={50}>
                <LineChart data={daily} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                  <YAxis hide domain={['dataMin', 'dataMax']} />
                  <XAxis dataKey="date" hide />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e2e8f0', padding: '4px 8px' }}
                    labelFormatter={d => format(parseISO(d), 'EEE d MMM')}
                    formatter={(v) => [`${v} hrs`, 'Avg']}
                  />
                  <Line
                    type="monotone"
                    dataKey="avg_hours"
                    stroke={CARRIER_COLOR[0]}
                    strokeWidth={1.75}
                    /* dot=true so a single-point series still renders — a
                       line with one node draws only if the dot is on. For
                       multi-point windows we hide the intermediate dots
                       via dot={{r:0}} so the line stays clean, keeping
                       just the endpoint marker via activeDot. */
                    dot={daily.length === 1 ? { r: 3, fill: CARRIER_COLOR[0] } : false}
                    activeDot={{ r: 3 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-[11px] text-slate-400 pt-3">No shipments in the last 30 days.</p>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// Tooltip for the horizontal top-destinations chart — count plus the
// percentage of total in-window volume so a small state's share is legible
// without having to hover-scan the axis.
function DestinationsTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null
  const row = payload[0].payload
  const fmtN = new Intl.NumberFormat('en-AU').format
  return (
    <div style={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', padding: '8px 10px' }}>
      <div className="text-slate-700 font-semibold mb-1 flex items-center gap-1.5">
        <MapPin size={12} className="text-slate-400" />
        {row.state}
      </div>
      <div className="text-slate-500 tabular-nums">
        {fmtN(row.count)} parcels · {row.pct.toFixed(1)}%
      </div>
    </div>
  )
}

// Top SKUs table. Two folds: first 10 by default, "Show more" reveals up
// to the full 20 the backend returns. Percentage cell doubles as a
// progress bar so a scroll-quick read of skew is possible without doing
// mental math on the number column.
function TopSkusCard({ skus, loading, error }) {
  const [showAll, setShowAll] = useState(false)
  const rows    = skus || []
  const visible = showAll ? rows.slice(0, 20) : rows.slice(0, 10)
  // Progress-bar width normalises against the top row so a 40% top SKU
  // becomes a full bar. Prevents the whole column from looking empty when
  // volume is spread across many SKUs.
  const maxPct  = rows.reduce((m, r) => Math.max(m, r.pct_of_units || 0), 0) || 1
  const fmtN    = new Intl.NumberFormat('en-AU').format
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
            <Package size={14} className="text-slate-400" />
            Top SKUs
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">By units shipped, last 30 days</p>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-[160px] text-slate-400 gap-2">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-xs">Loading…</span>
        </div>
      ) : error ? (
        // Ahead of the rows.length === 0 case — `skus || []` makes a failed
        // fetch look like a client that shipped nothing.
        <div className="flex items-center h-[120px]">
          <QueryError error={error} what="top SKUs" className="w-full" />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex items-center justify-center h-[120px] text-xs text-slate-400">
          No SKU activity in the last 30 days
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-400">
                <tr className="border-b border-slate-100">
                  <th className="text-left  font-medium pb-2 pr-2">SKU</th>
                  <th className="text-right font-medium pb-2 pr-3 whitespace-nowrap">Units</th>
                  <th className="text-right font-medium pb-2 pr-3 whitespace-nowrap">Orders</th>
                  <th className="text-left  font-medium pb-2 w-[40%] min-w-[120px]">% of volume</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {visible.map(r => (
                  <tr key={r.sku} className="border-b border-slate-50 last:border-b-0">
                    <td className="py-2 pr-2">
                      <div className="font-medium text-slate-800">{r.sku}</div>
                      {r.name && r.name !== r.sku && (
                        <div className="text-[11px] text-slate-400 truncate max-w-[280px]" title={r.name}>
                          {r.name}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmtN(r.units)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmtN(r.orders)}</td>
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${Math.min(100, (r.pct_of_units / maxPct) * 100)}%` }}
                          />
                        </div>
                        <span className="text-slate-500 tabular-nums w-10 text-right">
                          {(r.pct_of_units ?? 0).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 10 && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={() => setShowAll(v => !v)}
                className="text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                {showAll ? 'Show top 10' : `Show all ${Math.min(rows.length, 20)}`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// Custom tooltip for the parcels chart — shows the day's total, then
// per-carrier counts (Recharts default), and finally a service-level
// breakdown pulled from `services` (map keyed `date|carrier`).
function ParcelsTooltip({ active, payload, label, services }) {
  if (!active || !payload || !payload.length) return null
  const total = payload.reduce((s, p) => s + (p.value || 0), 0)
  const fmtN = new Intl.NumberFormat('en-AU').format
  return (
    <div style={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', padding: '8px 10px' }}>
      <div className="text-slate-700 font-semibold mb-1">
        {format(parseISO(label), 'EEE d MMM yyyy')}
      </div>
      <div className="text-slate-500 mb-2">Total {fmtN(total)}</div>
      {payload.filter(p => p.value > 0).map(p => {
        const svcs = services?.[`${label}|${p.dataKey}`] || []
        return (
          <div key={p.dataKey} className="mb-1 last:mb-0">
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
              <span className="text-slate-700">{p.dataKey}</span>
              <span className="text-slate-500 tabular-nums ml-auto pl-3">{fmtN(p.value)}</span>
            </div>
            {svcs.length > 0 && (
              <ul className="ml-4 mt-0.5">
                {svcs.map(s => (
                  <li key={s.service} className="flex justify-between text-slate-400">
                    <span>{s.service}</span>
                    <span className="tabular-nums pl-3">{fmtN(s.count)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Custom tooltip for the spend chart — shows per-bucket amount plus the
// stacked total, all formatted via the passed-in `fmt` (matches the
// OverviewTab AUD helper so currency renders identically everywhere).
function SpendTooltip({ active, payload, label, fmt }) {
  if (!active || !payload || !payload.length) return null
  const total = payload.reduce((s, p) => s + (p.value || 0), 0)
  return (
    <div style={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', padding: '8px 10px' }}>
      <div className="text-slate-700 font-semibold mb-1">
        {format(parseISO(label), 'EEE d MMM yyyy')}
      </div>
      <div className="text-slate-500 mb-2 tabular-nums">Total {fmt(total)}</div>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-center gap-1.5 mb-0.5 last:mb-0">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
          <span className="text-slate-700">{p.dataKey}</span>
          <span className="text-slate-500 tabular-nums ml-auto pl-3">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  )
}

function BreakdownRow({ label, value }) {
  return (
    <div className="flex justify-between text-xs text-slate-400">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-medium tabular-nums">{value || '—'}</span>
    </div>
  )
}

function TierTable({ tiers }) {
  if (!tiers || tiers.length === 0) return <p className="text-xs text-slate-400">No tiers configured.</p>
  // Tier schema uses min_orders / max_orders (max_orders is null on the
  // open-ended top band, which we render as ∞).
  return (
    <table className="w-full text-xs">
      <thead className="text-slate-400">
        <tr>
          <th className="text-left font-medium pb-1">From</th>
          <th className="text-left font-medium pb-1">To</th>
          <th className="text-right font-medium pb-1">Fee</th>
        </tr>
      </thead>
      <tbody className="text-slate-700">
        {tiers.map((t, i) => (
          <tr key={i} className="border-t border-slate-100">
            <td className="py-1">{t.min_orders ?? 0}</td>
            <td className="py-1">{t.max_orders == null || t.max_orders === '' ? '∞' : t.max_orders}</td>
            <td className="py-1 text-right tabular-nums">
              {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(t.fee ?? 0)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ─── Orders tab — reuses the admin OrderTable component with carrier cost hidden ───

function OrdersTab({ clientId }) {
  const [page, setPage]         = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [filters, setFilters]   = useState({})
  const [sort, setSort]         = useState({ col: 'shipped_date', dir: 'desc' })

  const params = useMemo(() => ({
    ...filters,
    limit: pageSize,
    offset: (page - 1) * pageSize,
    sort_by:  sort.col,
    sort_dir: sort.dir,
  }), [filters, page, pageSize, sort])

  const { data, isLoading } = useQuery({
    queryKey: ['me', 'orders', clientId, params],
    queryFn:  () => getMeOrders(clientId, params),
    enabled:  !!clientId,
    keepPreviousData: true,
  })

  const { data: carriers = [] } = useQuery({
    queryKey: ['me', 'orders', 'carriers', clientId],
    queryFn:  () => getMeOrderCarriers(clientId),
    enabled:  !!clientId,
  })
  const { data: states = [] } = useQuery({
    queryKey: ['me', 'orders', 'states', clientId],
    queryFn:  () => getMeOrderStates(clientId),
    enabled:  !!clientId,
  })

  // Pretty-format the period floor for the notice ("1 May 2026").
  const floorLabel = data?.period_floor
    ? new Date(`${data.period_floor}T00:00:00`).toLocaleDateString('en-AU', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : null

  return (
    <div className="space-y-3">
      {/* Open-cycle notice — explains why historical orders aren't visible.
          Past months become billed history once an invoice issues, after
          which they drop off this view automatically. */}
      {floorLabel && (
        <div className="text-xs text-slate-500 bg-blue-50/60 border border-blue-200 rounded px-3 py-2">
          Showing orders from <b>{floorLabel}</b> onwards — the open billing
          cycle. Once a month is invoiced, its orders move to your billing
          history and stop appearing here.
        </div>
      )}
      <FilterBar
        filters={filters}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }))
          setPage(1)
        }}
        onClear={() => { setFilters({}); setPage(1) }}
        carriers={carriers}
        states={states}
        /* Ship From falls back to period_floor when the user hasn't
           set an explicit filter — matches the "showing orders from …"
           notice above and stops the backend-enforced floor from being
           an invisible surprise. */
        minFrom={data?.period_floor}
      />
      <OrderTable
        orders={data?.orders || []}
        loading={isLoading}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        showCarrierCost={false}    /* censored — clients never see carrier cost */
        sort={sort}
        onSortChange={(col) => setSort(s => ({
          col, dir: s.col === col && s.dir === 'desc' ? 'asc' : 'desc',
        }))}
        getTrackingEvents={getMyOrderTrackingEvents}
      />
    </div>
  )
}
